import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../components/layout/AppShell';
import { PageHeader, DataTableToolbar, PaginationBar, EmptyState, LoadingSpinner } from '../components/layout/SharedComponents';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import InteractiveTable from '../components/InteractiveTable';
import { useFormatter } from '../utils/formatters';
import api, { getApiErrorMessage } from '../services/api';
import { toast } from 'sonner';
import { Plus, Download, Filter, LayoutDashboard, FileText } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import HelpButton from './Guides/HelpButton';
import SalesOrdersDashboard from './SalesOrdersDashboard';

const STATUS_STYLES = {
  DRAFT:       'bg-gray-100 text-gray-600 border-gray-200',
  SUBMITTED:   'bg-blue-100 text-blue-700 border-blue-200',
  APPROVED:    'bg-green-100 text-green-700 border-green-200',
  DISPATCHED:  'bg-cyan-100 text-cyan-700 border-cyan-200',
  INVOICED:    'bg-purple-100 text-purple-700 border-purple-200',
  CLOSED:      'bg-emerald-100 text-emerald-700 border-emerald-200',
  CANCELLED:   'bg-red-100 text-red-700 border-red-200',
  REJECTED:    'bg-orange-100 text-orange-700 border-orange-200',
};

export const SOStatusBadge = ({ status }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-[3px] text-[11px] font-medium ${
    STATUS_STYLES[status] || 'bg-gray-100 text-gray-600'
  }`}>
    <span className="w-1.5 h-1.5 rounded-full bg-current opacity-80 flex-shrink-0" />
    {status}
  </span>
);

export default function SalesOrdersPage() {
  const navigate = useNavigate();
  const { fmt } = useFormatter();
  const { systemSettings } = useAuth();
  const tz = systemSettings?.timezone || 'America/Los_Angeles';
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortState, setSortState] = useState({ field: 'so_number', dir: 'desc' });
  const handleSort = useCallback((field) => {
    setSortState(prev => prev.field === field ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'asc' });
  }, []);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/sales-orders', {
        params: { search, page, page_size: pageSize, sort_by: sortState.field, sort_dir: sortState.dir, status: statusFilter }
      });
      setRecords(res.data.data); setTotal(res.data.total);
    } catch (err) { if (err?.response?.status >= 500) toast.error('Failed to load sales orders'); }
    setLoading(false);
  }, [search, page, pageSize, sortState, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const fmtDate = d => { if (!d) return ''; try { return new Date(d).toLocaleDateString('en-GB', {day:'2-digit',month:'short',year:'numeric', timeZone: tz}); } catch { return d; } };

  const createNew = async () => {
    try {
      const res = await api.post('/api/sales-orders', {});
      navigate(`/sales-orders/${res.data.so_number}`);
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to create')); }
  };

  const soDefaultCols = useMemo(() => [
    { key: 'so_number', label: 'SO Number', width: 130, sortField: 'so_number', visible: true },
    { key: 'status', label: 'Status', width: 120, sortField: 'status', visible: true },
    { key: 'customer_name', label: 'Customer', width: 180, sortField: 'customer_name', visible: true },
    { key: 'description', label: 'Description', width: 200, sortField: 'description', visible: true },
    { key: 'order_date', label: 'Order Date', width: 120, sortField: 'order_date', visible: true },
    { key: 'required_date', label: 'Required By', width: 120, sortField: 'required_date', visible: true },
    { key: 'order_net_total', label: 'Net Total', width: 130, sortField: 'order_net_total', align: 'right', visible: true },
    { key: 'currency', label: 'Currency', width: 80, sortField: 'currency', visible: true },
    { key: 'salesperson', label: 'Salesperson', width: 140, sortField: 'salesperson', visible: true },
  ], []);

  const renderSoCell = useCallback((col, r) => {
    switch (col.key) {
      case 'so_number': return <span className="font-mono font-semibold text-zinc-900 text-sm">{r.so_number}</span>;
      case 'status': return <SOStatusBadge status={r.status} />;
      case 'customer_name': return <span className="font-medium text-sm">{r.customer_name}</span>;
      case 'description': return <span className="text-sm max-w-[200px] truncate block">{r.description}</span>;
      case 'order_date': return <span className="text-sm whitespace-nowrap">{fmtDate(r.order_date)}</span>;
      case 'required_date': return <span className="text-sm whitespace-nowrap">{fmtDate(r.required_date)}</span>;
      case 'order_net_total': return <span className="font-mono tabular-nums text-sm">{fmt(r.order_net_total)}</span>;
      case 'currency': return <span className="text-sm">{r.currency}</span>;
      case 'salesperson': return <span className="text-sm">{r.salesperson}</span>;
      default: return <span className="text-sm">{r[col.key] != null ? String(r[col.key]) : '—'}</span>;
    }
  }, [fmt, fmtDate]);

  const [viewTab, setViewTab] = useState('analytics');

  return (
    <AppShell>
      <PageHeader title="Sales Orders" description={`Total records: ${total}`} helpGuide="general"
        actions={viewTab === 'list' ? <>
          <Button size="sm" variant="outline"><Download className="h-4 w-4 mr-1" />Export</Button>
          <Button size="sm" onClick={createNew} data-testid="so-add-button"><Plus className="h-4 w-4 mr-1" />New Order</Button>
        </> : null}
      />

      <div className="flex gap-4 border-b mb-4" data-testid="so-view-tabs">
        <button onClick={() => setViewTab('analytics')} data-testid="so-tab-analytics"
          className={`pb-2 text-sm font-medium transition-colors ${viewTab === 'analytics' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
          <LayoutDashboard className="h-4 w-4 inline mr-1.5" /> Analytics
        </button>
        <button onClick={() => setViewTab('list')} data-testid="so-tab-list"
          className={`pb-2 text-sm font-medium transition-colors ${viewTab === 'list' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
          <FileText className="h-4 w-4 inline mr-1.5" /> Orders
        </button>
      </div>

      {viewTab === 'analytics' ? (
        <SalesOrdersDashboard />
      ) : (<>
      <div className="flex items-center gap-3 mb-4">
        <DataTableToolbar searchValue={search} onSearch={v=>{setSearch(v);setPage(1);}} onClear={()=>{setSearch('');setPage(1);}} searchPlaceholder="Search orders, customers..." toolbarSlotId="so-listing-toolbar" />
        <select value={statusFilter} onChange={e=>{setStatusFilter(e.target.value);setPage(1);}} className="h-9 rounded-lg border border-input bg-background px-3 text-sm">
          <option value="">All Statuses</option>
          {['DRAFT','SUBMITTED','APPROVED','DISPATCHED','INVOICED','CLOSED','CANCELLED'].map(s=><option key={s}>{s}</option>)}
        </select>
      </div>

      <div className="rounded-xl border bg-card overflow-hidden">
        {loading ? <div className="py-12"><LoadingSpinner /></div> :
        records.length === 0 ? <EmptyState title="No sales orders" description="Click New Order to get started." action={<Button size="sm" onClick={createNew}>New Order</Button>} /> : (
          <InteractiveTable
            tableId="so-listing"
            toolbarPortalId="so-listing-toolbar"
            defaultCols={soDefaultCols}
            data={records}
            renderCell={renderSoCell}
            onRowClick={(row) => navigate(`/sales-orders/${row.so_number}`)}
            sortState={sortState}
            onSort={handleSort}
            rowKey="so_number"
          />
        )}
        <div className="px-4"><PaginationBar page={page} pageSize={pageSize} total={total} onPageChange={setPage} onPageSizeChange={v=>{setPageSize(v);setPage(1);}} /></div>
      </div>
      </>)}
    </AppShell>
  );
}
